import { DiagnosticsLog } from '@/utils/diagnostics/diagnosticsLog';

/** События браузера, заменившие поток `connectivity_plus`. */
const ONLINE_EVENT = 'online';
const OFFLINE_EVENT = 'offline';

/**
 * Есть ли на устройстве сеть вообще: Wi-Fi, ethernet или мобильная.
 *
 * Нужен только для текста ошибок: «на планшете нет сети» и «сервер не
 * отвечает» — разные действия для человека у киоска. Запросы по нему
 * не блокируются: ложное «нет сети» не должно запереть планшет. Сервер
 * стоит в локальной сети и работает без интернета, поэтому проверяется
 * наличие подключения, а не доступ в интернет.
 *
 * В Dart состояние снимал `connectivity_plus`; в вебе тот же смысл у
 * `navigator.onLine` и событий `online`/`offline` — браузер тоже говорит
 * о наличии подключения, а не о доступности интернета.
 */
export class NetworkStatus {
  private constructor() {}

  static instance: NetworkStatus = new NetworkStatus();

  /** Экземпляр с заданным состоянием — для тестов. */
  static testInstance({ offline }: { offline: boolean }): NetworkStatus {
    const status = new NetworkStatus();
    status.#offline = offline;
    return status;
  }

  // Пока состояние не известно, считаем, что сеть есть: ошибки тогда
  // показываются как раньше, а не как ложное «включите Wi-Fi».
  #offline = false;

  /** Подписаны ли на события окна — вместо `StreamSubscription` в Dart. */
  #listening = false;

  get isOffline(): boolean {
    return this.#offline;
  }

  /**
   * Снимает текущее состояние и подписывается на изменения. Сбой
   * платформенного канала (тесты, десктоп) не мешает запуску.
   */
  async initialize(): Promise<void> {
    try {
      this.#apply(navigator.onLine);
      if (!this.#listening) {
        window.addEventListener(ONLINE_EVENT, this.#handleChange);
        window.addEventListener(OFFLINE_EVENT, this.#handleChange);
        this.#listening = true;
      }
    } catch (e) {
      this.#log(`состояние сети недоступно: ${String(e)}`);
    }
  }

  /**
   * Состояние берётся у `navigator`, а не из типа события: так один
   * обработчик отвечает и за `online`, и за `offline`.
   */
  readonly #handleChange = (): void => {
    try {
      this.#apply(navigator.onLine);
    } catch (e) {
      this.#log(`поток состояния сети: ${String(e)}`);
    }
  };

  #apply(online: boolean): void {
    const offline = !online;
    if (offline !== this.#offline) {
      this.#log(offline ? 'сети нет' : 'сеть появилась');
      DiagnosticsLog.instance.event(offline ? 'сети нет' : 'сеть появилась', {
        isError: offline,
      });
    }
    this.#offline = offline;
  }

  /** Аналог `dev.log(message, name: 'NetworkStatus')` из `dart:developer`. */
  #log(message: string): void {
    console.debug(`[NetworkStatus] ${message}`);
  }

  async dispose(): Promise<void> {
    // Не подписывались — снимать нечего (в Dart здесь был `_subscription?.`).
    if (!this.#listening) return;
    window.removeEventListener(ONLINE_EVENT, this.#handleChange);
    window.removeEventListener(OFFLINE_EVENT, this.#handleChange);
    this.#listening = false;
  }
}
